import { useRef, useState } from 'react';
import { DndContext, useSensors } from '@dnd-kit/core';
import { useQueryClient } from '@tanstack/react-query';
import { PageShell } from '../components/PageShell';
import { CardPrazo } from '../components/CardPrazo';
import { api } from '../api';
import { useAnaliseInicial, useCasosKanban, useCriarCaso, usePlanoPro, usePode } from '../hooks';
import type { ColunaCards } from '../types';

/**
 * Aba "Petições Iniciais" (fluxo de caso novo) — kanban dos cards que nascem de
 * UPLOAD de documentos (não do DJEN) p/ gerar uma petição inicial.
 *
 * FASE 1 (esta): criar caso (cliente/título + tribunal) + upload múltiplo de
 * documentos + gating por capacidade. Os botões Análise inicial → Gerar rascunho
 * → Refinar (Fase 2) entram depois, reusando o pipeline/refino existentes.
 *
 * Gating: a aba é cosmética; a trava real é o backend (require_capacidade nos
 * endpoints /api/casos). Sem `aba.peticoes_iniciais`, /api/casos/kanban dá 403.
 */

const EXT_OK = ['.pdf', '.docx', '.doc', '.odt', '.rtf', '.txt', '.png', '.jpg', '.jpeg'];
const MAX_MB = 200;

// Tribunais do Trabalho (TRT1..TRT24) — vara/tribunal é POR PROCESSO, não por tenant.
const TRIBUNAIS = Array.from({ length: 24 }, (_, i) => `TRT${i + 1}`);

function validarArquivo(f: File): string | null {
  const ext = '.' + (f.name.split('.').pop()?.toLowerCase() ?? '');
  if (!EXT_OK.includes(ext)) return `Extensão ${ext} não aceita`;
  if (f.size > MAX_MB * 1024 * 1024) return `${f.name}: maior que ${MAX_MB} MB`;
  return null;
}

/** Valida cada arquivo do lote independentemente — mantém os válidos mesmo
 * quando algum vem inválido junto (drag-drop/seleção múltipla), em vez de
 * descartar o lote inteiro no primeiro erro. */
function separarValidos(arquivos: File[]): { validos: File[]; erros: string[] } {
  const validos: File[] = [];
  const erros: string[] = [];
  for (const f of arquivos) {
    const e = validarArquivo(f);
    if (e) erros.push(e); else validos.push(f);
  }
  return { validos, erros };
}

/** Categorias dos documentos iniciais do cliente — puramente organizacional: o
 * upload continua indo pro MESMO endpoint (upload-peticao), só prefixamos o
 * nome do arquivo com a categoria (visível no Drive e no prompt da análise
 * inicial, que lista os documentos pelo nome). */
const CATEGORIAS: { tag: string; label: string }[] = [
  { tag: 'Anamnese', label: '🩺 Ficha de anamnese' },
  { tag: 'Contrato', label: '📄 Contrato de trabalho' },
  { tag: 'CTPS', label: '📘 Carteira de trabalho (CTPS)' },
  { tag: 'Holerites', label: '💰 Holerites' },
  { tag: 'Ponto', label: '🕐 Cartão de ponto' },
  { tag: 'TRCT', label: '📋 TRCT (rescisão)' },
  { tag: 'FGTS', label: '🏦 Extrato de FGTS' },
  { tag: 'AvisoPrevio', label: '📨 Aviso prévio' },
  { tag: 'Ferias', label: '🏖️ Recibos de férias' },
  { tag: 'Outros', label: '📎 Outros documentos' },
];

/** File.name é read-only — recria o File com o nome prefixado pela categoria. */
function comCategoria(f: File, tag: string): File {
  return new File([f], `[${tag}] ${f.name}`, { type: f.type });
}

/** Grade de 4 dropzones compactas (uma por categoria) — cada uma chama
 * `onArquivos` com os arquivos JÁ renomeados com o prefixo da categoria. */
function CategoriasDropzone({
  onArquivos,
  disabled,
}: {
  onArquivos: (arquivos: File[]) => void;
  disabled?: boolean;
}) {
  const refs = useRef<Record<string, HTMLInputElement | null>>({});

  function processar(tag: string, lista: FileList | null) {
    if (!lista || !lista.length) return;
    onArquivos(Array.from(lista).map((f) => comCategoria(f, tag)));
  }

  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
      {CATEGORIAS.map(({ tag, label }) => (
        <div
          key={tag}
          onClick={() => !disabled && refs.current[tag]?.click()}
          onDragOver={(e) => e.preventDefault()}
          onDrop={(e) => { e.preventDefault(); if (!disabled) processar(tag, e.dataTransfer.files); }}
          className={`border-2 border-dashed rounded-lg p-2.5 text-center text-[11px] leading-tight transition ${
            disabled
              ? 'border-slate-200 dark:border-slate-800 text-slate-300 dark:text-slate-700 cursor-not-allowed'
              : 'border-slate-300 dark:border-slate-700 text-slate-500 dark:text-slate-400 cursor-pointer hover:border-blue-400'
          }`}
        >
          {label}
          <input
            ref={(el) => { refs.current[tag] = el; }}
            type="file"
            multiple
            accept={EXT_OK.join(',')}
            className="hidden"
            disabled={disabled}
            onChange={(e) => { processar(tag, e.target.files); e.target.value = ''; }}
          />
        </div>
      ))}
    </div>
  );
}

/** Sobe os arquivos em sequência para o caso. Usa o endpoint dedicado
 * /api/casos/{id}/documentos, que grava TODO documento (qualquer extensão) na
 * pasta local do tenant — de onde a Análise inicial lê. (O upload-peticao antigo
 * só gravava .pdf localmente; fotos de CTPS, .docx e imagens sumiam.) */
async function subirDocumentos(
  idPrazo: number,
  arquivos: File[],
  onStep?: (nome: string, i: number, total: number) => void,
): Promise<void> {
  for (let i = 0; i < arquivos.length; i++) {
    onStep?.(arquivos[i].name, i + 1, arquivos.length);
    await api.uploadDocumentoCaso(idPrazo, arquivos[i]);
  }
}

function ModalNovoCaso({ onClose, onCriado }: { onClose: () => void; onCriado: (idPrazo: number) => void }) {
  const qc = useQueryClient();
  const criar = useCriarCaso();
  const [reclamante, setReclamante] = useState('');
  const [reclamada, setReclamada] = useState('');
  const [tribunal, setTribunal] = useState('TRT3');
  const [erro, setErro] = useState<string | null>(null);

  async function salvar() {
    if (!reclamante.trim()) { setErro('Informe o nome do reclamante (cliente).'); return; }
    if (!reclamada.trim()) { setErro('Informe o nome da reclamada (parte adversa).'); return; }
    setErro(null);
    try {
      // O caso é rotulado como "Reclamante × Reclamada" (campo titulo do backend).
      const titulo = `${reclamante.trim()} × ${reclamada.trim()}`;
      const card = await criar.mutateAsync({ titulo, sigla_tribunal: tribunal });
      qc.invalidateQueries({ queryKey: ['casos'] });
      // Cria o card e já abre o painel de documentos do novo caso (o usuário sobe
      // os docs em seguida e dispara a Análise inicial de lá).
      onCriado(card.id_prazo);
    } catch (e: any) {
      setErro(e?.status === 403
        ? 'Sem permissão para criar casos neste escritório.'
        : (e?.message ?? 'Falha ao criar o caso.'));
    }
  }

  const ocupado = criar.isPending;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
      <div
        className="w-full max-w-lg bg-white dark:bg-slate-900 rounded-2xl shadow-2xl border border-slate-200 dark:border-slate-800 p-5"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 className="font-display font-bold text-lg text-slate-800 dark:text-slate-100 mb-4">
          ➕ Novo caso (petição inicial)
        </h2>

        <label className="block text-xs font-semibold text-slate-600 dark:text-slate-300 mb-1">
          Reclamante (cliente)
        </label>
        <input
          autoFocus
          value={reclamante}
          onChange={(e) => setReclamante(e.target.value)}
          placeholder="Ex.: Fulano da Silva"
          className="w-full mb-3 px-3 py-2 rounded-lg border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-800 text-sm dark:text-slate-100"
        />

        <label className="block text-xs font-semibold text-slate-600 dark:text-slate-300 mb-1">
          Reclamada (parte adversa)
        </label>
        <input
          value={reclamada}
          onChange={(e) => setReclamada(e.target.value)}
          placeholder="Ex.: ACME Ltda"
          className="w-full mb-3 px-3 py-2 rounded-lg border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-800 text-sm dark:text-slate-100"
        />

        <label className="block text-xs font-semibold text-slate-600 dark:text-slate-300 mb-1">
          Tribunal (a vara é definida no processo)
        </label>
        <select
          value={tribunal}
          onChange={(e) => setTribunal(e.target.value)}
          className="w-full mb-4 px-3 py-2 rounded-lg border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-800 text-sm dark:text-slate-100"
        >
          {TRIBUNAIS.map((t) => <option key={t} value={t}>{t}</option>)}
        </select>

        {erro && <p className="mt-1 mb-2 text-xs text-red-600 dark:text-red-400">{erro}</p>}

        <div className="flex justify-end gap-2 mt-3">
          <button
            onClick={onClose}
            disabled={ocupado}
            className="px-4 py-2 text-sm rounded-lg text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800"
          >Cancelar</button>
          <button
            onClick={salvar}
            disabled={ocupado}
            className="px-4 py-2 text-sm rounded-lg bg-blue-600 hover:bg-blue-700 text-white font-medium disabled:opacity-60"
          >{ocupado ? '⏳ Criando…' : 'Criar caso'}</button>
        </div>
      </div>
    </div>
  );
}

/** Painel de upload de documentos + disparo da Análise inicial, para um card
 * já existente. Fica aberto entre uploads (o usuário pode subir várias
 * categorias antes de disparar a análise). */
function PainelUpload({ idPrazo, onClose }: { idPrazo: number; onClose: () => void }) {
  const qc = useQueryClient();
  const planoPro = usePlanoPro();
  const [erro, setErro] = useState<string | null>(null);
  const [progresso, setProgresso] = useState<string | null>(null);
  const [enviados, setEnviados] = useState<string[]>([]);
  const analise = useAnaliseInicial();
  const [statusAnalise, setStatusAnalise] = useState<'idle' | 'ok' | 'erro'>('idle');
  const [msgAnalise, setMsgAnalise] = useState<string | null>(null);

  async function enviar(arquivos: File[]) {
    const { validos, erros } = separarValidos(arquivos);
    setErro(erros.length ? erros.join(' · ') : null);
    if (!validos.length) return;
    try {
      await subirDocumentos(idPrazo, validos, (nome, i, total) =>
        setProgresso(`Enviando ${i}/${total}: ${nome}`));
      setEnviados((prev) => [...prev, ...validos.map((f) => f.name)]);
      setProgresso(null);
      qc.invalidateQueries({ queryKey: ['casos'] });
    } catch (e: any) {
      setProgresso(null);
      setErro(e?.message ?? 'Falha no upload.');
    }
  }

  async function fazerAnaliseInicial() {
    setStatusAnalise('idle');
    setMsgAnalise(null);
    try {
      await analise.mutateAsync(idPrazo);
      setStatusAnalise('ok');
      setMsgAnalise('Análise iniciada — acompanhe o progresso no card. Quando concluir, '
        + 'o botão "✏️ Refinar" aparece nele.');
    } catch (e: any) {
      setStatusAnalise('erro');
      setMsgAnalise(e?.message ?? 'Falha ao disparar a análise inicial.');
    }
  }

  const ocupado = progresso !== null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
      <div
        className="w-full max-w-xl bg-white dark:bg-slate-900 rounded-2xl shadow-2xl border border-slate-200 dark:border-slate-800 p-5"
        onClick={(e) => e.stopPropagation()}
      >
        <h3 className="font-display font-bold text-base text-slate-800 dark:text-slate-100 mb-3">
          📎 Documentos do cliente
        </h3>
        <CategoriasDropzone disabled={ocupado} onArquivos={enviar} />
        {erro && <p className="mt-3 text-xs text-red-600 dark:text-red-400">{erro}</p>}
        {progresso && <p className="mt-3 text-xs text-blue-600 dark:text-blue-300">{progresso}</p>}
        {enviados.length > 0 && (
          <p className="mt-2 text-[11px] text-slate-400 dark:text-slate-500">
            ✓ {enviados.length} documento(s) enviado(s) nesta sessão.
          </p>
        )}

        <div className="mt-4 pt-4 border-t border-slate-200 dark:border-slate-800">
          <button
            onClick={fazerAnaliseInicial}
            disabled={!planoPro || analise.isPending || ocupado}
            className="w-full px-4 py-2 text-sm rounded-lg bg-violet-600 hover:bg-violet-700 disabled:opacity-60 text-white font-medium"
            title={!planoPro
              ? 'Análise inicial com IA é um recurso do plano Pro'
              : ocupado
              ? 'Aguarde o upload terminar'
              : 'Extrai o texto dos documentos já subidos e gera o rascunho da petição inicial com IA'}
          >
            {!planoPro ? '⭐ Análise inicial (Pro)'
              : analise.isPending ? '⏳ Analisando…'
              : statusAnalise === 'ok' ? '✅ Análise disparada!'
              : statusAnalise === 'erro' ? '❌ Falhou — tentar de novo'
              : '🔍 Fazer análise inicial'}
          </button>
          {msgAnalise && (
            <p className={`mt-2 text-[11px] ${statusAnalise === 'erro' ? 'text-red-600 dark:text-red-400' : 'text-violet-600 dark:text-violet-300'}`}>
              {msgAnalise}
            </p>
          )}
        </div>

        <div className="flex justify-end mt-4">
          <button onClick={onClose} className="px-4 py-2 text-sm rounded-lg text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800">Fechar</button>
        </div>
      </div>
    </div>
  );
}

export function PeticoesIniciais() {
  const pode = usePode();
  const { data: colunas, isLoading, isError } = useCasosKanban();
  const [novoCaso, setNovoCaso] = useState(false);
  const [uploadId, setUploadId] = useState<number | null>(null);

  const podeAbrir = pode('aba.peticoes_iniciais');
  const podeCriar = pode('item.caso_criar');

  if (!podeAbrir) {
    return (
      <PageShell titulo={<h1 className="font-display font-bold text-slate-700 dark:text-slate-200">📑 Petições Iniciais</h1>}>
        <p className="text-center text-slate-500 dark:text-slate-400 p-8">
          🔒 Esta aba não está habilitada para o seu perfil neste escritório.
        </p>
      </PageShell>
    );
  }

  const ordem: ColunaCards['coluna'][] = ['nao_feitos', 'em_andamento', 'pronto_protocolo', 'concluidos'];
  const porColuna = new Map((colunas ?? []).map((c) => [c.coluna, c]));

  return (
    // DndContext com sensores vazios: CardPrazo usa useDraggable (precisa do provider),
    // mas o drag fica desabilitado (draggable=false) — toda ação é por botão.
    <DndContext sensors={useSensors()}>
    <PageShell
      titulo={<h1 className="font-display font-bold text-slate-700 dark:text-slate-200">📑 Petições Iniciais</h1>}
      acoes={podeCriar ? (
        <button
          onClick={() => setNovoCaso(true)}
          className="px-3 py-1.5 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium"
        >➕ Novo caso</button>
      ) : null}
    >
      {isLoading && <p className="text-sm text-slate-400">Carregando casos…</p>}
      {isError && <p className="text-sm text-red-500">Não foi possível carregar os casos.</p>}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
        {ordem.map((k) => {
          const col = porColuna.get(k);
          const cards = col?.cards ?? [];
          return (
            <section key={k} className="bg-slate-50 dark:bg-slate-900/40 rounded-xl border border-slate-200 dark:border-slate-800 flex flex-col">
              <header className="px-3 py-2 border-b border-slate-200 dark:border-slate-800 flex items-center justify-between">
                <span className="text-xs font-bold uppercase tracking-wide text-slate-600 dark:text-slate-300">
                  {col?.titulo ?? k}
                </span>
                <span className="text-[10px] font-mono text-slate-400">{cards.length}</span>
              </header>
              <div className="p-2 space-y-2 min-h-[120px]">
                {cards.length > 0 ? (
                  cards.map((c) => (
                    <CardPrazo
                      key={c.id_prazo}
                      card={c}
                      draggable={false}
                      // O painel de upload/análise inicial só faz sentido enquanto o
                      // caso ainda está sendo produzido — em Pronto p/ protocolo e
                      // Protocolados não há o que subir/analisar mais (evita abrir o
                      // diálogo errado num card já avançado no pipeline).
                      onClick={(k === 'nao_feitos' || k === 'em_andamento')
                        ? () => setUploadId(c.id_prazo)
                        : undefined}
                    />
                  ))
                ) : (
                  <p className="text-[11px] text-slate-300 dark:text-slate-600 italic px-1 py-2">—</p>
                )}
              </div>
            </section>
          );
        })}
      </div>

      {novoCaso && (
        <ModalNovoCaso
          onClose={() => setNovoCaso(false)}
          onCriado={(id) => { setNovoCaso(false); setUploadId(id); }}
        />
      )}
      {uploadId !== null && <PainelUpload idPrazo={uploadId} onClose={() => setUploadId(null)} />}
    </PageShell>
    </DndContext>
  );
}
